import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiArrowRight, HiChevronDown } from "react-icons/hi2";
import { TrainingLog, Session } from "../../types/post";
import { useAuth } from "../../contexts/AuthContext";
import SessionActivitiesTable from "../sessions/SessionActivitiesTable";
import { SessionNotes } from "../sessions/SessionNotesMarkdown";
import { useActivityNameResolver } from "../../hooks/useActivityNameResolver";
import { getSessionDurationMinutes } from "../../utils/sessionRules";

import LogCard from "../shared/LogCard";
import {
  LogCardChip,
  LogCardMetricsRow,
  LogCardMetric,
  LogCardNote,
} from "../shared/LogCardContent";

interface TrainingLogCardProps {
  log: TrainingLog & { id: string };
  /** Every session linked to the log, primary first (plan 3a). */
  linkedSessions?: (Session & { id: string })[];
  /** @deprecated Use `linkedSessions`; kept for single-session callers. */
  linkedSession?: Session & { id: string };
  ownerUserId?: string;
}

const TrainingLogCard: React.FC<TrainingLogCardProps> = ({
  log,
  linkedSessions,
  linkedSession,
  ownerUserId,
}) => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);

  // Stable identities: the name resolver keys its fetch effects on the
  // measurements array.
  const sessions = useMemo<(Session & { id: string })[]>(
    () => linkedSessions ?? (linkedSession ? [linkedSession] : []),
    [linkedSessions, linkedSession],
  );
  const allMeasurements = useMemo(() => {
    const all = sessions.flatMap((s) => s.ActivityMeasurementList ?? []);
    return all.length > 0 ? all : undefined;
  }, [sessions]);

  const { getActivityName, loading: loadingActivityNames } =
    useActivityNameResolver(allMeasurements, ownerUserId);

  const handleCardClick = () => {
    if (userData?.casualMode) return;
    setIsExpanded((prev) => !prev);
  };

  const handleViewLogClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/insights/trainingLog/${log.id}`);
  };

  const handleViewSessionClick = (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    navigate(`/session/${sessionId}`);
  };

  const getProgressColor = (value: number) => {
    if (value <= 2) return "bg-blue-500";
    if (value <= 4) return "bg-green-500";
    if (value <= 6) return "bg-orange-600";
    if (value <= 8) return "bg-orange-700";
    if (value <= 9) return "bg-red-500";
    return "bg-red-700";
  };

  const renderProgressBar = (
    label: string,
    value: number,
    maxValue: number = 10,
  ) => (
    <div className="mb-2">
      <div className="flex justify-between items-center mb-1">
        <span className="text-[11px] text-base-content/60 font-geist">
          {label}
        </span>
        <span className="text-[11px] font-geist text-base-content">
          <strong>{value}</strong>/{maxValue}
        </span>
      </div>
      <div className="w-full bg-base-300 rounded-full h-1.5">
        <div
          className={`h-1.5 rounded-full transition-all duration-300 ${getProgressColor(value)}`}
          style={{ width: `${(value / maxValue) * 100}%` }}
        />
      </div>
    </div>
  );

  return (
    <div
      onClick={handleCardClick}
      className={userData?.casualMode ? "" : "cursor-pointer"}
    >
      <div className="relative">
        <button
          onClick={handleViewLogClick}
          className="absolute top-2 right-2 z-10 p-1.5 hover:bg-base-200 rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          aria-label="View training log details"
        >
          <HiArrowRight className="w-3.5 h-3.5 text-base-content/50" />
        </button>

        <LogCard className="bg-base-200 border border-base-300">
          {/* Header row */}
          <div className="flex items-center gap-2 pr-8">
            <span className="text-[11px] font-medium tracking-wide uppercase text-base-content/70 font-geist">
              Training
            </span>
            {log.duration > 0 && (
              <span className="text-xs text-base-content/70 font-geist">
                {log.duration}min
              </span>
            )}
            {sessions.length > 1 && (
              <span className="text-xs text-base-content/60 font-geist">
                · {sessions.length} sessions
              </span>
            )}
            {!userData?.casualMode && (
              <HiChevronDown
                className={`w-3 h-3 text-base-content/30 transition-transform duration-200 ml-auto mr-4 ${
                  isExpanded ? "rotate-180" : ""
                }`}
              />
            )}
          </div>

          {/* Activity tags */}
          {log.tag && log.tag.length > 0 && (
            <div className="mt-1.5">
              <LogCardMetricsRow gap={1}>
                {log.tag.map((tag, index) => (
                  <LogCardChip key={index} label={tag} />
                ))}
              </LogCardMetricsRow>
            </div>
          )}

          {/* Notes */}
          {log.notes && <LogCardNote text={log.notes} />}

          {/* Expanded details */}
          {!userData?.casualMode && isExpanded && (
            <div className="mt-3 space-y-1.5">
              <div className="bg-base-200 rounded-lg p-3 space-y-1">
                {renderProgressBar("Muscular", log.muscular)}
                {renderProgressBar("Respiratory", log.respiratory)}
              </div>
              <LogCardMetric value={log.load} label="Load" />
              <LogCardMetric value={log.rpe} label="RPE" />
              <LogCardMetric value={`${log.duration}min`} label="Duration" />
              {log.trainingPerformed !== undefined && (
                <LogCardMetric
                  value={log.trainingPerformed}
                  label="Performance"
                />
              )}

              {/* Linked session details, one block per session */}
              {sessions.map((session, index) => {
                const measurements = session.ActivityMeasurementList ?? [];
                const hasNotes = (session.activityBlocks || []).some(
                  (b) =>
                    b.blockType === "notes" && (b.notesContent || "").trim(),
                );
                const minutes = getSessionDurationMinutes(session);
                const heading =
                  sessions.length > 1
                    ? session.name?.trim() || `Session ${index + 1}`
                    : "Session details";
                return (
                  <div
                    key={session.id}
                    className="bg-base-200 rounded-lg p-3 space-y-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-medium tracking-wide uppercase text-base-content/70 font-geist truncate">
                        {heading}
                        {sessions.length > 1 && index === 0 && (
                          <span className="ml-1.5 normal-case tracking-normal text-base-content/50">
                            primary
                          </span>
                        )}
                        {minutes > 0 && (
                          <span className="ml-1.5 normal-case tracking-normal text-base-content/50">
                            {minutes} min
                          </span>
                        )}
                      </span>
                      <button
                        onClick={(e) => handleViewSessionClick(e, session.id)}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary flex-shrink-0"
                        aria-label="View session details"
                      >
                        View session
                        <HiArrowRight className="w-3 h-3" />
                      </button>
                    </div>

                    <SessionNotes blocks={session.activityBlocks} />

                    {measurements.length > 0 ? (
                      loadingActivityNames ? (
                        <div className="text-center py-2">
                          <span className="loading loading-spinner loading-xs"></span>
                        </div>
                      ) : (
                        <SessionActivitiesTable
                          measurements={measurements}
                          getActivityName={getActivityName}
                        />
                      )
                    ) : (
                      !hasNotes && (
                        <p className="text-xs text-base-content/50">
                          No activity details logged.
                        </p>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </LogCard>
      </div>
    </div>
  );
};

export default TrainingLogCard;
